import { Component } from '@theme/component';

/**
 * Horizontal scroll rail with explicit, cross-browser scroll controls.
 *
 * Ways to move the rail: native touch swipe, mouse wheel, prev/next arrows,
 * click-and-drag the cards (mouse), and dragging the progress scrubber (any
 * pointer). State is reflected on the host so CSS can show edge fades, the
 * scrubber, arrows and a swipe hint.
 *
 * Reflected on the host element:
 *   [data-scrollable] — content overflows (there is somewhere to scroll)
 *   [data-at-start]   — scrolled fully to the start
 *   [data-at-end]     — scrolled fully to the end
 *
 * @extends {Component<{ viewport: HTMLElement; track: HTMLElement; thumb: HTMLElement }>}
 */
class ScrollRail extends Component {
  /** @type {string[]} */
  requiredRefs = ['viewport', 'track', 'thumb'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} */
  #frame = 0;

  /** @type {HTMLElement | null} */
  #prevBtn = null;

  /** @type {HTMLElement | null} */
  #nextBtn = null;

  // Click-and-drag the cards (mouse only — touch uses native scrolling).
  /** @type {number | null} */
  #dragPointerId = null;
  #dragStartX = 0;
  #dragStartScroll = 0;
  #dragging = false;
  #suppressClick = false;

  // Drag the progress scrubber (any pointer type).
  #scrubbing = false;

  connectedCallback() {
    super.connectedCallback();

    const { viewport, track } = this.refs;

    viewport.addEventListener('scroll', this.#onScroll, { passive: true });
    viewport.addEventListener('wheel', this.#onWheel, { passive: false });
    viewport.addEventListener('pointerdown', this.#onRailPointerDown);
    // Capture phase: swallow the click that follows a drag so card buttons/
    // links aren't triggered when the user was actually dragging.
    viewport.addEventListener('click', this.#onClickCapture, true);
    viewport.addEventListener('dragstart', this.#preventDefault);

    track.addEventListener('pointerdown', this.#onScrubPointerDown);

    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);

    // Wire the arrows directly (not via on:click) so the click still resolves
    // even though the <iconify-icon> inside each button lives in shadow DOM.
    this.#prevBtn = this.querySelector('[data-rail-prev]');
    this.#nextBtn = this.querySelector('[data-rail-next]');
    this.#prevBtn?.addEventListener('click', this.prev);
    this.#nextBtn?.addEventListener('click', this.next);

    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(viewport);

    this.#update();
    // Re-measure after first paint, once card widths have settled.
    requestAnimationFrame(this.#update);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { viewport, track } = this.refs;
    viewport?.removeEventListener('scroll', this.#onScroll);
    viewport?.removeEventListener('wheel', this.#onWheel);
    viewport?.removeEventListener('pointerdown', this.#onRailPointerDown);
    viewport?.removeEventListener('click', this.#onClickCapture, true);
    viewport?.removeEventListener('dragstart', this.#preventDefault);
    track?.removeEventListener('pointerdown', this.#onScrubPointerDown);

    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);

    this.#prevBtn?.removeEventListener('click', this.prev);
    this.#nextBtn?.removeEventListener('click', this.next);
    this.#resizeObserver?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  // ── Arrows ───────────────────────────────────────────────────────────────
  prev = () => this.#page(-1);
  next = () => this.#page(1);

  /** @param {number} direction */
  #page(direction) {
    const { viewport } = this.refs;
    if (!viewport) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    viewport.scrollBy({ left: direction * viewport.clientWidth * 0.8, behavior });
  }

  // ── Click-and-drag the cards (mouse) ──────────────────────────────────────
  /** @param {PointerEvent} event */
  #onRailPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const { viewport } = this.refs;
    if (!viewport) return;
    this.#dragPointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartScroll = viewport.scrollLeft;
    this.#dragging = false;
  };

  // ── Drag the progress scrubber (any pointer) ──────────────────────────────
  /** @param {PointerEvent} event */
  #onScrubPointerDown = (event) => {
    event.preventDefault();
    this.#scrubbing = true;
    this.refs.track?.classList.add('is-scrubbing');
    this.refs.viewport?.classList.add('is-dragging'); // disable snap for a smooth scrub
    this.#scrubTo(event.clientX);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#scrubbing) {
      this.#scrubTo(event.clientX);
      return;
    }

    if (this.#dragPointerId === null) return;
    const { viewport } = this.refs;
    if (!viewport) return;

    const dx = event.clientX - this.#dragStartX;
    if (!this.#dragging) {
      // Below the threshold this is still a click — leave card buttons alone.
      if (Math.abs(dx) < 6) return;
      this.#dragging = true;
      viewport.classList.add('is-dragging');
    }
    viewport.scrollLeft = this.#dragStartScroll - dx;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#scrubbing) {
      this.#scrubbing = false;
      this.refs.track?.classList.remove('is-scrubbing');
      this.refs.viewport?.classList.remove('is-dragging');
      return;
    }

    if (this.#dragPointerId === null) return;
    if (this.#dragging) {
      this.refs.viewport?.classList.remove('is-dragging');
      // The browser fires a click right after this drag — swallow it once.
      this.#suppressClick = true;
      setTimeout(() => {
        this.#suppressClick = false;
      }, 0);
    }
    this.#dragPointerId = null;
    this.#dragging = false;
  };

  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {Event} event */
  #preventDefault = (event) => event.preventDefault();

  /** @param {number} clientX */
  #scrubTo(clientX) {
    const { viewport, track, thumb } = this.refs;
    if (!viewport || !track || !thumb) return;

    const rect = track.getBoundingClientRect();
    const thumbWidth = thumb.getBoundingClientRect().width;
    const usable = rect.width - thumbWidth;
    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    if (usable <= 0 || maxScroll <= 0) return;

    let pos = clientX - rect.left - thumbWidth / 2;
    pos = Math.max(0, Math.min(usable, pos));
    viewport.scrollLeft = (pos / usable) * maxScroll;
  }

  // ── Wheel → horizontal ────────────────────────────────────────────────────
  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    const { viewport } = this.refs;
    if (!viewport) return;

    // Leave horizontal-intent gestures (trackpads) to the browser.
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    if (maxScroll <= 1) return;

    // Release to normal page scrolling once we're against the relevant end.
    const atStart = viewport.scrollLeft <= 0;
    const atEnd = viewport.scrollLeft >= maxScroll - 1;
    if ((event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) return;

    event.preventDefault();
    viewport.scrollLeft += event.deltaY;
  };

  // ── State reflection + scrubber sizing ────────────────────────────────────
  // rAF-throttle the scroll work so it stays smooth.
  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update = () => {
    const { viewport, track, thumb } = this.refs;
    if (!viewport || !track || !thumb) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    const scrollable = maxScroll > 1;

    // Reflect state first so the (CSS-hidden) scrubber is laid out before we measure it.
    this.toggleAttribute('data-scrollable', scrollable);
    this.toggleAttribute('data-at-start', viewport.scrollLeft <= 1);
    this.toggleAttribute('data-at-end', viewport.scrollLeft >= maxScroll - 1);

    if (!scrollable) return;

    // Size and position the progress thumb to mirror the viewport.
    const trackWidth = track.clientWidth;
    const thumbWidth = Math.max(trackWidth * (viewport.clientWidth / viewport.scrollWidth), 28);
    const offset = (viewport.scrollLeft / maxScroll) * (trackWidth - thumbWidth);

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${offset}px)`;
  };
}

if (!customElements.get('scroll-rail')) {
  customElements.define('scroll-rail', ScrollRail);
}
